import React, {
  FC,
  useEffect,
  useState,
  ChangeEvent,
  useCallback,
  useMemo,
} from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { InputText } from 'primereact/inputtext';
import { Select } from 'components/UI/Select/Select';
import { UserSearchType } from 'models/restTypes';
import { useStores } from 'stores/hooks/hooks';
import { DropdownChangeEvent } from 'primereact/dropdown';
import debounce from 'lodash.debounce';

const SearchContainer = styled.div`
  align-self: flex-start;
  display: flex;
  gap: 0.5rem;
`;

const StyledInputText = styled(InputText)`
  width: 250px;
`;

const StyledSelect = styled(Select)`
  width: 150px;
`;

const UserSearchInput: FC = observer(() => {
  const { t } = useTranslation('users');
  const { membersStore } = useStores();
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<UserSearchType>(
    UserSearchType.EMAIL,
  );

  const [isLoading, setIsLoading] = useState(false);

  const searchTypeOptions = [
    { label: 'Email', value: UserSearchType.EMAIL },
    { label: 'Phone', value: UserSearchType.PHONE },
    { label: 'Name', value: UserSearchType.NAME },
  ];

  const handleInputChanged = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
    },
    [],
  );

  const handleChangeSelect = (event: DropdownChangeEvent) => {
    setSearchType(event.value as UserSearchType);
  };

  const debouncedResults = useMemo(() => {
    return debounce(handleInputChanged, 1000);
  }, [handleInputChanged]);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  }, [debouncedResults]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      membersStore.setMembers([]);
      return;
    }

    setIsLoading(true);
    membersStore
      .getSearchUsers(searchQuery, searchType)
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, [searchQuery, searchType, membersStore]);

  return (
    <SearchContainer>
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          width: '150px',
        }}
      >
        <StyledSelect
          value={searchType}
          options={searchTypeOptions}
          onChange={handleChangeSelect}
        />
      </div>
      <StyledInputText
        // value={inputValue}
        onChange={debouncedResults}
        placeholder={t('search')}
      />
    </SearchContainer>
  );
});

export default UserSearchInput;
